<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import WysiwygEditor from '../components/WysiwygEditor.vue'
import paymentDocumentService, { type PaymentDocument } from '../services/payment-document.service'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const clubUuid = route.params.uuid as string
const documentUuid = route.params.documentUuid as string

const document = ref<PaymentDocument | null>(null)
const form = ref({
  title: '',
  name: '',
  content: ''
})

const loading = ref(false)
const loadingDocument = ref(true)
const error = ref<string | null>(null)

const pageTitle = computed(() => {
  if (!document.value) return t('paymentDocumentEdit.editDocument')

  switch (document.value.type) {
    case 'terms':
      return t('paymentDocumentEdit.editTerms')
    case 'privacy':
      return t('paymentDocumentEdit.editPrivacy')
    case 'other':
      return t('paymentDocumentEdit.editDocument')
    default:
      return t('paymentDocumentEdit.editDocument')
  }
})

const needsName = computed(() => document.value?.type === 'other')

const isVersioned = computed(() => {
  return document.value?.type === 'terms' || document.value?.type === 'privacy'
})

const validateForm = (): string | null => {
  if (needsName.value && !form.value.name.trim()) {
    return t('paymentDocumentEdit.nameRequired')
  }

  if (!form.value.content.trim() || form.value.content === '<p></p>') {
    return t('paymentDocumentEdit.contentRequired')
  }

  return null
}

const loadDocument = async () => {
  loadingDocument.value = true
  error.value = null

  try {
    const response = await paymentDocumentService.getDocument(clubUuid, documentUuid)
    document.value = response.document

    form.value.title = response.document.title || ''
    form.value.name = response.document.name || ''
    form.value.content = response.document.content || ''
  } catch (err: any) {
    console.error('Error loading document:', err)
    error.value = err.response?.data?.message || t('paymentDocumentEdit.errorLoading')
  } finally {
    loadingDocument.value = false
  }
}

const handleSave = async () => {
  error.value = null

  const validationError = validateForm()
  if (validationError) {
    error.value = validationError
    return
  }

  loading.value = true

  try {
    const data = {
      title: needsName.value
        ? form.value.name.trim()
        : (form.value.title.trim() || undefined),
      content: form.value.content,
      ...(needsName.value && { name: form.value.name.trim() })
    }

    await paymentDocumentService.updateDocument(clubUuid, documentUuid, data)

    router.push(`/club/${clubUuid}/payment-settings`)
  } catch (err: any) {
    console.error('Error updating document:', err)
    error.value = err.response?.data?.message || t('paymentDocumentEdit.errorSaving')
  } finally {
    loading.value = false
  }
}

const handleCancel = () => {
  router.push(`/club/${clubUuid}/payment-settings`)
}

onMounted(() => {
  loadDocument()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-6">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="handleCancel"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('paymentDocumentEdit.backToSettings') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900">{{ pageTitle }}</h1>
        <div v-if="document && isVersioned" class="mt-2 flex items-center gap-2">
          <span class="text-sm text-gray-600">{{ t('paymentDocumentEdit.currentVersion') }} {{ document.version }}</span>
          <span class="text-xs text-gray-500 bg-yellow-100 px-2 py-1 rounded">
            {{ t('paymentDocumentEdit.saveWillCreateVersion', { version: document.version + 1 }) }}
          </span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loadingDocument" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <div class="flex items-center justify-center py-12">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">{{ t('paymentDocumentEdit.loadingDocument') }}</p>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-else-if="error && !document" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
        <p class="text-sm">{{ error }}</p>
        <button
          @click="handleCancel"
          class="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
          {{ t('paymentDocumentEdit.backToSettings2') }}
        </button>
      </div>

      <!-- Form -->
      <div v-else-if="document" class="space-y-4">
        <!-- Error Message (during save) -->
        <div v-if="error" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <p class="text-sm">{{ error }}</p>
        </div>

        <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
          <form @submit.prevent="handleSave" class="space-y-6">
            <!-- Title -->
            <div v-if="document.type !== 'other'">
              <label for="title" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentDocumentEdit.documentTitleLabel') }}
              </label>
              <input
                id="title"
                v-model="form.title"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="document.title"
              />
            </div>

            <!-- Name (for "other" type) -->
            <div v-if="needsName">
              <label for="name" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentDocumentEdit.documentNameLabel') }} <span class="text-red-600">*</span>
              </label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                :placeholder="t('paymentDocumentEdit.documentNamePlaceholder')"
              />
            </div>

            <!-- Content (WYSIWYG Editor) -->
            <div>
              <label for="content" class="block text-sm font-medium text-gray-700 mb-2">
                {{ t('paymentDocumentEdit.contentLabel') }} <span class="text-red-600">*</span>
              </label>
              <WysiwygEditor
                v-model="form.content"
                :placeholder="t('paymentDocumentEdit.contentPlaceholder')"
              />
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                @click="handleCancel"
                :disabled="loading"
                class="px-6 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ t('common.cancel') }}
              </button>
              <button
                type="submit"
                :disabled="loading"
                class="px-6 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ loading ? t('paymentDocumentEdit.saving') : t('common.save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </main>
</template>
